import React, { useState, useEffect } from "react";
import { Search, Tables } from "@deeptrue-frontend/ui";
import Menu from "../Menu";
import styled from "styled-components/macro";
import { useDebounce } from "hooks/useDebouce";
import RouterProvider from "components/RouterProvider/RouterProvider";
import { RouteComponentProps } from "react-router-dom";
import queryString from "query-string";
import { defaultLimit } from "pages/OrganizationAdmin/Projects";
import { ProjectType } from "pages/OrganizationAdmin/types";
import Project from "./Projects";
import { useFormatMessage } from "components/IntlProvider";

const { ROUTES, withRouter } = RouterProvider;

const AllProjects = styled.span`
  fontsize: 12px;
  color: gray;
`;

const HeaderWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 1rem;
  width: 100%;
  align-items: center;
`;

const { Table, TableBody, TableHeader, TablePagination, TableFooter } = Tables;

const thCells = (t: any) => [
  {
    text: t("organization.columns.name", "Name"),
    orderName: "name",
    props: {
      minWidth: "180px",
      padding: "1rem 0.5rem",
      height: "3rem !important",
    },
  },
  {
    text: t("organization.columns.product", "Product"),
    orderName: "product_name",
    props: {
      minWidth: "210px",
      padding: "1rem 0.5rem",
      height: "3rem !important",
    },
  },
  {
    text: t("organization.columns.groups", "Groups"),
    props: {
      minWidth: "150px",
      padding: "1rem",
      height: "3rem !important",
    },
  },
  {
    text: t("organization.columns.status", "Status"),
    orderName: "status",
    props: {
      minWidth: "100px",
      padding: "1rem 0.5rem",
      height: "3rem !important",
    },
  },

  {
    text: t("organization.columns.resources", "Resources"),
    props: {
      minWidth: "80px",
      padding: "1rem 0.5rem",
      height: "3rem !important",
    },
  },
];

interface Props extends RouteComponentProps {
  isLoaded: boolean;
  count: number;
  currentPage: number;
  orderBy?: string;
  sortColumns: (colName: string) => void;
  projects: ProjectType[];
}

const ProjectsList = ({
  isLoaded,
  count,
  currentPage,
  history,
  orderBy,
  sortColumns,
  location,
  projects,
}: Props) => {
  const [search, setSearchValue] = useState("");
  const searchValue = useDebounce(search);
  const parsedSearch = queryString.parse(location.search);
  const t = useFormatMessage();

  useEffect(() => {
    const params = {
      limit: defaultLimit,
      offset: 0,
      search: searchValue,
      ordering: parsedSearch.ordering,
    };
    history.push(
      `${ROUTES.ORGANIZATION.ADMIN.PROJECTS}?${queryString.stringify(params)}`
    );
    // eslint-disable-next-line
  }, [searchValue]);

  const onChangePage = (page: number) => {
    const params = {
      limit: defaultLimit,
      offset: defaultLimit * page - defaultLimit,
      search: searchValue,
      ordering: parsedSearch.ordering,
    };
    history.push(
      `${ROUTES.ORGANIZATION.ADMIN.PROJECTS}?${queryString.stringify(params)}`
    );
  };

  return (
    <>
      <Menu />
      <Search
        placeholder={t("organization.search", "Search...")}
        onChange={(e) => setSearchValue(e.target.value as string)}
      />

      <HeaderWrapper>
        <AllProjects>
          {count}{" "}
          {t("organization.withinOrganization", "{name} withing organization", {
            name: t("organization.projects", "Projects"),
          })}
        </AllProjects>
      </HeaderWrapper>
      <Table>
        <TableHeader
          cells={thCells(t)}
          orderBy={orderBy}
          onOrdering={sortColumns}
        />
        <TableBody
          withLoader
          isLoading={!isLoaded}
          isData={count > 0}
          noDataText={t(
            "noresults",
            "No results. We have nothing to show here."
          )}
        >
          {projects.map((project: ProjectType) => {
            return (
              <Project
                key={project.id}
                name={project.name}
                productName={project.product_name}
                status={project.status as number}
                id={project.id as number}
                sharedGroups={project.shared_to_groups}
              />
            );
          })}
        </TableBody>

        <TableFooter>
          <TablePagination
            count={count}
            currentPage={currentPage}
            rowsPerPage={defaultLimit}
            onChangePage={onChangePage}
          />
        </TableFooter>
      </Table>
    </>
  );
};

export default withRouter(ProjectsList);
